import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { projectsApi, type Project, type ProjectLimits, type ProjectRole } from '../services/projectsApi';
import { subscriptionService, type ProjectAddons } from '../services/subscriptionService';
import { useTenant } from './TenantContext';
import { useAuth } from './AuthContext';
import { supabase, ensureValidSession } from '../lib/supabase';

interface ProjectContextType {
  currentProject: Project | null;
  allProjects: Project[];
  selectedProjectId: string | null;
  setSelectedProjectId: (projectId: string | null) => void;
  isLoadingProjects: boolean;
  canCreateProject: boolean;
  projectLimits: ProjectLimits | null;
  projectAddons: ProjectAddons | null;
  userProjectRole: ProjectRole | null;
  refreshProjects: () => Promise<void>;
  hasProjectAccess: (projectId: string) => boolean;
  updateAddonQuantity: (newQuantity: number) => Promise<{ success: boolean; error?: string }>;
  showAddonModal: boolean;
  setShowAddonModal: (show: boolean) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

interface ProjectProviderProps {
  children: ReactNode;
}

const SELECTED_PROJECT_KEY = 'rewardpulse_selected_project';

export const ProjectProvider = ({ children }: ProjectProviderProps) => {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectIdState] = useState<string | null>(() => {
    return localStorage.getItem(SELECTED_PROJECT_KEY);
  });
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [projectLimits, setProjectLimits] = useState<ProjectLimits | null>(null);
  const [projectAddons, setProjectAddons] = useState<ProjectAddons | null>(null);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [userProjectRole, setUserProjectRole] = useState<ProjectRole | null>(null);
  const [userProjectAccess, setUserProjectAccess] = useState<Map<string, ProjectRole>>(new Map());
  const prevTenantIdRef = useRef<string | null | undefined>(undefined);

  const canCreateProject = projectLimits?.projects.unlimited ||
    (projectLimits?.projects.remaining ?? 0) > 0;

  const loadProjects = useCallback(async () => {
    if (!tenant?.id) {
      setAllProjects([]);
      setCurrentProject(null);
      setProjectLimits(null);
      setProjectAddons(null);
      setIsLoadingProjects(false);
      return;
    }

    const tenantChanged = prevTenantIdRef.current !== tenant.id;
    prevTenantIdRef.current = tenant.id;

    if (tenantChanged) {
      setSelectedProjectIdState(null);
      localStorage.removeItem(SELECTED_PROJECT_KEY);
    }

    try {
      setIsLoadingProjects(true);

      const hasSession = await ensureValidSession();
      if (!hasSession) {
        setIsLoadingProjects(false);
        return;
      }

      const [projects, limits, addons] = await Promise.all([
        projectsApi.fetchProjects(tenant.id),
        projectsApi.getTenantLimits(tenant.id),
        subscriptionService.getProjectAddons(tenant.id)
      ]);

      setAllProjects(projects);
      setProjectLimits(limits);
      setProjectAddons(addons);

      if (projects.length > 0) {
        let selectedProject: Project;

        if (tenantChanged || user?.isMegaAdmin) {
          selectedProject = projects.find(p => p.is_default) || projects[0];
        } else {
          const storedProjectId = localStorage.getItem(SELECTED_PROJECT_KEY);
          if (storedProjectId) {
            const storedProject = projects.find(p => p.id === storedProjectId);
            selectedProject = storedProject || projects.find(p => p.is_default) || projects[0];
          } else {
            selectedProject = projects.find(p => p.is_default) || projects[0];
          }
        }

        setCurrentProject(selectedProject);
        setSelectedProjectIdState(selectedProject.id);
        localStorage.setItem(SELECTED_PROJECT_KEY, selectedProject.id);

        if (!user?.isMegaAdmin) {
          const accessMap = new Map<string, ProjectRole>();
          projects.forEach(p => {
            accessMap.set(p.id, 'editor');
          });
          setUserProjectAccess(accessMap);
          setUserProjectRole(accessMap.get(selectedProject.id) || null);
        } else {
          setUserProjectRole('owner');
        }
      } else {
        setCurrentProject(null);
        setSelectedProjectIdState(null);
        localStorage.removeItem(SELECTED_PROJECT_KEY);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setIsLoadingProjects(false);
    }
  }, [tenant?.id, user?.isMegaAdmin]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const tenantPlanChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!tenant?.id) return;

    tenantPlanChannelRef.current = supabase
      .channel(`tenant-plan-${tenant.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tenants',
          filter: `id=eq.${tenant.id}`,
        },
        () => {
          loadProjects();
        }
      )
      .subscribe();

    return () => {
      if (tenantPlanChannelRef.current) {
        supabase.removeChannel(tenantPlanChannelRef.current);
        tenantPlanChannelRef.current = null;
      }
    };
  }, [tenant?.id, loadProjects]);

  const setSelectedProjectId = useCallback((projectId: string | null) => {
    if (!projectId) {
      setSelectedProjectIdState(null);
      setCurrentProject(null);
      localStorage.removeItem(SELECTED_PROJECT_KEY);
      return;
    }

    const targetProject = allProjects.find(p => p.id === projectId);
    if (!targetProject) {
      return;
    }

    setSelectedProjectIdState(projectId);
    setCurrentProject(targetProject);
    localStorage.setItem(SELECTED_PROJECT_KEY, projectId);

    if (user?.isMegaAdmin) {
      setUserProjectRole('owner');
    } else {
      setUserProjectRole(userProjectAccess.get(projectId) || null);
    }
  }, [allProjects, user?.isMegaAdmin, userProjectAccess]);

  const hasProjectAccess = useCallback((projectId: string): boolean => {
    if (user?.isMegaAdmin) return true;
    return userProjectAccess.has(projectId);
  }, [user?.isMegaAdmin, userProjectAccess]);

  const refreshProjects = useCallback(async () => {
    await loadProjects();
  }, [loadProjects]);

  const updateAddonQuantity = useCallback(async (newQuantity: number): Promise<{ success: boolean; error?: string }> => {
    if (!tenant?.id) return { success: false, error: 'No tenant selected' };

    if (projectLimits && newQuantity < (projectLimits.projects.addon_quantity || 0)) {
      const baseLimit = projectLimits.projects.base_limit;
      const newMax = baseLimit + newQuantity;
      const activeCount = projectLimits.projects.current;
      if (activeCount > newMax) {
        return {
          success: false,
          error: `You have ${activeCount} active projects. Please archive ${activeCount - newMax} project(s) first to reduce to ${newQuantity} add-on slot(s).`
        };
      }
    }

    const result = await subscriptionService.updateProjectAddonQuantity(tenant.id, newQuantity);
    if (result.success) {
      await loadProjects();
    }
    return result;
  }, [tenant?.id, projectLimits, loadProjects]);

  return (
    <ProjectContext.Provider value={{
      currentProject,
      allProjects,
      selectedProjectId,
      setSelectedProjectId,
      isLoadingProjects,
      canCreateProject,
      projectLimits,
      projectAddons,
      userProjectRole,
      refreshProjects,
      hasProjectAccess,
      updateAddonQuantity,
      showAddonModal,
      setShowAddonModal
    }}>
      {children}
    </ProjectContext.Provider>
  );
};
